import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AutoAwesome, CalendarMonth } from "@mui/icons-material";
import ApiHandler from "../../../Handler/ApiHandler";
import { formatDuration } from "../../../Common/DurationCommon";
import moment from "moment";
import "./public-appointment.css";
import { Tooltip } from "@mui/material";

const formatPrice = (price) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number(price || 0));

function PublicSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  groupBy,
  groupFallbackLabel = "Autres prestations",
}) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    const onClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const selected = options.find((item) => String(item.value) === String(value));
  const groupedOptions = React.useMemo(() => {
    if (typeof groupBy !== "function") {
      return [];
    }

    const groups = options.reduce((acc, item) => {
      const rawLabel = groupBy(item);
      const label = String(rawLabel || groupFallbackLabel).trim() || groupFallbackLabel;
      if (!acc[label]) {
        acc[label] = [];
      }
      acc[label].push(item);
      return acc;
    }, {});

    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }))
      .map((groupLabel) => ({
        label: groupLabel,
        options: groups[groupLabel].sort((a, b) =>
          String(a.label || "").localeCompare(String(b.label || ""), "fr", { sensitivity: "base" })
        ),
      }));
  }, [groupBy, groupFallbackLabel, options]);

  return (
    <div className={`public-select ${open ? "is-open" : ""} ${disabled ? "is-disabled" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="public-select-trigger"
        onClick={() => !disabled && setOpen((prev) => !prev)}
      >
        <span className={!selected ? "is-placeholder" : ""}>
          {selected ? selected.label : placeholder}
        </span>
      </button>

      {open && !disabled && (
        <div className="public-select-menu">
          {options.length < 1 && <p className="public-select-empty">Aucune option</p>}
          {groupedOptions.length > 0
            ? groupedOptions.map((group) => (
                <div key={`group-${group.label}`} className="public-select-group">
                  <p className="public-select-group-label">{group.label}</p>
                  {group.options.map((item) => (
                    <button
                      type="button"
                      key={item.value}
                      className={`public-select-option ${String(item.value) === String(value) ? "is-active" : ""}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onChange(item.value);
                        setOpen(false);
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              ))
            : options.map((item) => (
                <button
                  type="button"
                  key={item.value}
                  className={`public-select-option ${String(item.value) === String(value) ? "is-active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(item.value);
                    setOpen(false);
                  }}
                >
                  {item.label}
                </button>
              ))}
        </div>
      )}
    </div>
  );
}

function PublicDatePicker({ value, onChange, minDate }) {
  const [open, setOpen] = React.useState(false);
  const [viewMonth, setViewMonth] = React.useState(() =>
    value ? moment(value, "YYYY-MM-DD") : moment()
  );
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    const onClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  React.useEffect(() => {
    if (value) {
      setViewMonth(moment(value, "YYYY-MM-DD"));
    }
  }, [value]);

  const min = minDate ? moment(minDate, "YYYY-MM-DD") : null;
  const start = viewMonth.clone().startOf("month").startOf("week");
  const end = viewMonth.clone().endOf("month").endOf("week");
  const days = [];
  const cursor = start.clone();

  while (cursor.isSameOrBefore(end, "day")) {
    days.push(cursor.clone());
    cursor.add(1, "day");
  }

  return (
    <div className={`public-date-picker ${open ? "is-open" : ""}`} ref={rootRef}>
      <button type="button" className="public-date-trigger" onClick={() => setOpen((prev) => !prev)}>
        <span className={value ? "" : "is-placeholder"}>
          {value ? moment(value, "YYYY-MM-DD").format("DD/MM/YYYY") : "Selectionnez une date"}
        </span>
        <CalendarMonth fontSize="small" />
      </button>

      {open && (
        <div className="public-date-menu">
          <div className="public-date-header">
            <button type="button" onClick={() => setViewMonth((prev) => prev.clone().subtract(1, "month"))}>
              {"<"}
            </button>
            <strong>{viewMonth.format("MMMM YYYY")}</strong>
            <button type="button" onClick={() => setViewMonth((prev) => prev.clone().add(1, "month"))}>
              {">"}
            </button>
          </div>

          <div className="public-date-weekdays">
            {["L", "M", "M", "J", "V", "S", "D"].map((day, index) => (
              <span key={`${day}-${index}`}>{day}</span>
            ))}
          </div>

          <div className="public-date-grid">
            {days.map((day) => {
              const isCurrentMonth = day.isSame(viewMonth, "month");
              const isSelected = value && day.isSame(moment(value, "YYYY-MM-DD"), "day");
              const isDisabled = min && day.isBefore(min, "day");

              return (
                <button
                  type="button"
                  key={day.format("YYYY-MM-DD")}
                  className={`public-date-day ${!isCurrentMonth ? "muted" : ""} ${isSelected ? "is-selected" : ""}`}
                  disabled={!!isDisabled}
                  onClick={() => {
                    onChange(day.format("YYYY-MM-DD"));
                    setOpen(false);
                  }}
                >
                  {day.date()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ComplementPicker({ options, value, onChange, disabled = false }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    const onClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const selectedItems = React.useMemo(
    () => options.filter((item) => value.includes(item.value)),
    [options, value]
  );

  const availableItems = React.useMemo(
    () => options.filter((item) => !value.includes(item.value)),
    [options, value]
  );

  return (
    <div className={`complement-picker ${open ? "is-open" : ""} ${disabled ? "is-disabled" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="complement-picker-trigger"
        onClick={() => !disabled && setOpen((prev) => !prev)}
      >
        {selectedItems.length > 0
          ? `${selectedItems.length} complement(s) selectionne(s)`
          : "Ajouter un complement"}
      </button>

      {open && !disabled && (
        <div className="complement-picker-menu">
          {availableItems.length < 1 && (
            <p className="complement-picker-empty">Tous les complements sont deja ajoutes.</p>
          )}
          {availableItems.map((item) => (
            <button
              type="button"
              key={item.value}
              className="complement-picker-option"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange([...value, item.value]);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {selectedItems.length > 0 && (
        <div className="complement-picker-tags">
          {selectedItems.map((item) => (
            <button
              type="button"
              key={`tag-${item.value}`}
              className="complement-picker-tag"
              onClick={() => onChange(value.filter((id) => id !== item.value))}
            >
              <span>{item.label}</span>
              <strong>x</strong>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PublicAppointment() {
  const { prestationId } = useParams();
  const navigate = useNavigate();
  const captchaRef = React.useRef(null);

  const [loading, setLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [successId, setSuccessId] = React.useState(null);
  const [error, setError] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [slotsLoading, setSlotsLoading] = React.useState(false);
  const [prestations, setPrestations] = React.useState([]);
  const [slots, setSlots] = React.useState([]);
  const [firstAppointmentBonusComplements, setFirstAppointmentBonusComplements] = React.useState([]);
  const [showFirstAppointmentPopup, setShowFirstAppointmentPopup] = React.useState(false);
  const [emailBlurLookupVersion, setEmailBlurLookupVersion] = React.useState(0);
  const hasShownFirstAppointmentPopupRef = React.useRef(false);
  const lastBlurredEmailRef = React.useRef("");
  const [form, setForm] = React.useState({
    firstname: "",
    lastname: "",
    email: "",
    telephone: "",
    prestationId: prestationId || "",
    complementIds: [],
    date: "",
    startAt: "",
    note: "",
    acceptLegalTerms: false,
  });
  const [captcha, setCaptcha] = React.useState({
    enabled: false,
    siteKey: "",
    scriptReady: false,
    widgetId: null,
    token: "",
  });
  const today = React.useMemo(() => new Date().toISOString().slice(0, 10), []);
  const telephoneRegex = /^(0[1-9][0-9]{8}|(?:\+33|0033)[1-9][0-9]{8})$/;
  const normalizedEmail = React.useMemo(
    () => String(form.email || "").trim().toLowerCase(),
    [form.email]
  );
  const clearFieldError = React.useCallback((field) => {
    setFieldErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }

      const next = { ...previous };
      delete next[field];
      return next;
    });
  }, []);
  const renderCaptchaWidget = React.useCallback((previousWidgetId = null) => {
    if (!captchaRef.current || !window.turnstile?.render || !captcha.siteKey) {
      return null;
    }

    if (previousWidgetId !== null && window.turnstile?.remove) {
      try {
        window.turnstile.remove(previousWidgetId);
      } catch (_error) {
        // Ignore removal errors and retry render in a clean container.
      }
    }

    captchaRef.current.innerHTML = "";

    const widgetId = window.turnstile.render(captchaRef.current, {
      sitekey: captcha.siteKey,
      callback: (token) => {
        clearFieldError("captcha");
        setCaptcha((prev) => ({ ...prev, token }));
      },
      "expired-callback": () => {
        setCaptcha((prev) => ({ ...prev, token: "" }));
      },
      "error-callback": () => {
        setCaptcha((prev) => ({ ...prev, token: "" }));
      },
    });

    setCaptcha((prev) => ({ ...prev, widgetId, token: "" }));
    return widgetId;
  }, [captcha.siteKey, clearFieldError]);

  React.useEffect(() => {
    ApiHandler.get({ route: "public_appointment_select" }, (response) => {
      if (response.status === 200) {
        const prestationOptions = response.data?.prestations || [];
        setPrestations(prestationOptions);

        setForm((prev) => {
          const hasPresetPrestation = prestationOptions.some(
            (item) => String(item.value) === String(prev.prestationId)
          );
          if (prev.prestationId && hasPresetPrestation) {
            return prev;
          }

          return {
            ...prev,
            prestationId: prestationOptions[0]?.value ? String(prestationOptions[0].value) : "",
            complementIds: [],
          };
        });
      } else {
        setError("Impossible de charger les prestations.");
      }
      setLoading(false);
    });
  }, [prestationId]);

  React.useEffect(() => {
    ApiHandler.get({ route: "public_appointment_captcha_config" }, (response) => {
      if (response.status === 200) {
        setCaptcha((prev) => ({
          ...prev,
          enabled: !!response.data?.enabled,
          siteKey: response.data?.siteKey || "",
        }));
      }
    });
  }, []);

  React.useEffect(() => {
    if (!captcha.enabled || !captcha.siteKey) {
      return undefined;
    }

    let cancelled = false;
    const markReady = () => {
      if (cancelled) return;
      setCaptcha((prev) => (prev.scriptReady ? prev : { ...prev, scriptReady: true }));
    };

    if (window.turnstile?.render) {
      markReady();
      return () => {
        cancelled = true;
      };
    }

    let pollTimer = null;
    let script = document.getElementById("turnstile-script");
    const onLoad = () => {
      if (script) {
        script.dataset.loaded = "true";
      }
      markReady();
    };

    if (!script) {
      script = document.createElement("script");
      script.id = "turnstile-script";
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.addEventListener("load", onLoad);
      document.body.appendChild(script);
    } else {
      if (script.dataset.loaded === "true") {
        markReady();
      }
      script.addEventListener("load", onLoad);
    }

    // Handles cases where the script is already loaded and no new load event fires.
    pollTimer = window.setInterval(() => {
      if (window.turnstile?.render) {
        window.clearInterval(pollTimer);
        markReady();
      }
    }, 200);

    return () => {
      cancelled = true;
      script?.removeEventListener("load", onLoad);
      if (pollTimer) {
        window.clearInterval(pollTimer);
      }
    };
  }, [captcha.enabled, captcha.siteKey]);

  React.useEffect(() => {
    if (!captcha.enabled || !captcha.scriptReady || !captcha.siteKey || !captchaRef.current) {
      return;
    }

    if (captcha.widgetId !== null) {
      return;
    }

    if (!window.turnstile?.render) {
      return;
    }
    renderCaptchaWidget();
  }, [captcha.enabled, captcha.scriptReady, captcha.siteKey, captcha.widgetId, renderCaptchaWidget]);

  React.useEffect(() => {
    return () => {
      if (captcha.widgetId !== null && window.turnstile?.remove) {
        window.turnstile.remove(captcha.widgetId);
      }
    };
  }, [captcha.widgetId]);

  React.useEffect(() => {
    if (!form.date || !form.prestationId) {
      setSlots([]);
      setSlotsLoading(false);
      return;
    }

    if (form.date < today) {
      setSlots([]);
      setSlotsLoading(false);
      return;
    }

    setSlotsLoading(true);
    ApiHandler.get(
      {
        route: "public_appointment_slots",
        data: {
          date: form.date,
          prestationId: form.prestationId,
          complementIds: form.complementIds,
        },
      },
      (response) => {
        if (response.status === 200) {
          setSlots(response.data?.slots || []);
        } else {
          setSlots([]);
        }
        setSlotsLoading(false);
      }
    );
  }, [form.date, form.prestationId, form.complementIds, today]);

  React.useEffect(() => {
    const emailToLookup = String(lastBlurredEmailRef.current || "");
    const isBlurredEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToLookup);
    if (!emailBlurLookupVersion || !form.prestationId || !isBlurredEmailValid) {
      setFirstAppointmentBonusComplements([]);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      ApiHandler.get(
        {
          route: "public_appointment_first_appointment_benefits",
          data: {
            email: emailToLookup,
            prestationId: form.prestationId,
          },
        },
        (response) => {
          if (cancelled) {
            return;
          }

          if (response.status !== 200) {
            setFirstAppointmentBonusComplements([]);
            return;
          }

          const complements = Array.isArray(response.data?.complements) ? response.data.complements : [];
          setFirstAppointmentBonusComplements(complements);

          const hasFirstAppointmentBenefits = response.data?.hasFirstAppointmentBenefits === true
            || ((response.data?.isFirstAppointment === true) && complements.length > 0);
          if (hasFirstAppointmentBenefits && !hasShownFirstAppointmentPopupRef.current) {
            hasShownFirstAppointmentPopupRef.current = true;
            setShowFirstAppointmentPopup(true);
          }
        }
      );
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [emailBlurLookupVersion, form.prestationId]);

  const selectedPrestation = React.useMemo(
    () => prestations.find((item) => String(item.value) === String(form.prestationId)),
    [prestations, form.prestationId]
  );
  const availableComplements = React.useMemo(
    () => {
      const baseComplements = Array.isArray(selectedPrestation?.complements) ? selectedPrestation.complements : [];
      const merged = new Map();

      [...baseComplements, ...firstAppointmentBonusComplements].forEach((item) => {
        if (!item || item.value === undefined || item.value === null) {
          return;
        }

        merged.set(String(item.value), item);
      });

      return Array.from(merged.values());
    },
    [selectedPrestation, firstAppointmentBonusComplements]
  );

  React.useEffect(() => {
    const allowedValues = new Set(availableComplements.map((item) => String(item.value)));

    setForm((prev) => {
      const currentValues = Array.isArray(prev.complementIds) ? prev.complementIds : [];
      const nextValues = currentValues.filter((value) => allowedValues.has(String(value)));

      if (nextValues.length === currentValues.length) {
        return prev;
      }

      return {
        ...prev,
        complementIds: nextValues,
        startAt: "",
      };
    });
  }, [availableComplements]);
  const selectedComplements = React.useMemo(
    () => availableComplements.filter((item) => form.complementIds.includes(item.value)),
    [availableComplements, form.complementIds]
  );
  const totalDuration = React.useMemo(() => {
    const mainDuration = Number(selectedPrestation?.duration || 0);
    const complementsDuration = selectedComplements.reduce(
      (total, item) => total + Number(item.duration || 0),
      0
    );
    return mainDuration + complementsDuration;
  }, [selectedPrestation, selectedComplements]);
  const totalPrice = React.useMemo(() => {
    const mainPrice = Number(selectedPrestation?.price || 0);
    const complementsPrice = selectedComplements.reduce(
      (total, item) => total + Number(item.price || 0),
      0
    );
    return mainPrice + complementsPrice;
  }, [selectedPrestation, selectedComplements]);

  const setValue = (key, value) => {
    clearFieldError(key);
    if (key === "prestationId" || key === "date" || key === "complementIds") {
      clearFieldError("startAt");
    }
    if (key === "acceptLegalTerms") {
      clearFieldError("acceptLegalTerms");
    }
    if (key === "email") {
      lastBlurredEmailRef.current = "";
      setFirstAppointmentBonusComplements([]);
    }

    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "prestationId" ? { complementIds: [], startAt: "" } : {}),
      ...(key === "date" || key === "complementIds" ? { startAt: "" } : {}),
    }));
  };

  const submit = () => {
    setError("");
    setFieldErrors({});

    const nextFieldErrors = {};
    if (!form.firstname) nextFieldErrors.firstname = "Ce champ est obligatoire.";
    if (!form.lastname) nextFieldErrors.lastname = "Ce champ est obligatoire.";
    if (!form.email) nextFieldErrors.email = "Ce champ est obligatoire.";
    if (!form.prestationId) nextFieldErrors.prestationId = "Merci de sélectionner une prestation.";
    if (!form.date) nextFieldErrors.date = "Merci de sélectionner une date.";
    if (!form.startAt) nextFieldErrors.startAt = "Merci de sélectionner un créneau.";

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setError("Merci de renseigner tous les champs obligatoires.");
      return;
    }

    if (form.date && form.date < today) {
      setFieldErrors({ date: "Impossible de réserver une date passée." });
      setError("Impossible de réserver une date passée.");
      return;
    }

    const normalizedTelephone = form.telephone.replace(/[\s.\-()]/g, "");
    if (normalizedTelephone && !telephoneRegex.test(normalizedTelephone)) {
      setFieldErrors({ telephone: "Numéro de téléphone invalide. Ex: 0612345678 ou +33612345678." });
      setError("Numéro de téléphone invalide. Ex: 0612345678 ou +33612345678.");
      return;
    }

    if (!form.acceptLegalTerms) {
      setFieldErrors({
        acceptLegalTerms: "Vous devez accepter les mentions légales et les CGU pour confirmer le rendez-vous.",
      });
      setError("Vous devez accepter les mentions légales et les CGU pour confirmer le rendez-vous.");
      return;
    }

    if (captcha.enabled && !captcha.token) {
      if (captcha.scriptReady && window.turnstile?.render) {
        renderCaptchaWidget(captcha.widgetId);
        setFieldErrors({
          captcha: "Merci de valider le CAPTCHA avant de confirmer le rendez-vous.",
        });
        setError("Le CAPTCHA a été rechargé. Merci de le valider avant de confirmer le rendez-vous.");
        return;
      }
      setFieldErrors({
        captcha: "Merci de valider le CAPTCHA avant de confirmer le rendez-vous.",
      });
      setError("Merci de valider le CAPTCHA avant de confirmer le rendez-vous.");
      return;
    }

    setIsSaving(true);
    ApiHandler.post(
      {
        route: "public_appointment_add",
        data: {
          firstname: form.firstname,
          lastname: form.lastname,
          email: form.email,
          telephone: form.telephone || null,
          prestationId: form.prestationId,
          complementIds: form.complementIds,
          startAt: form.startAt,
          note: form.note || null,
          acceptLegalTerms: form.acceptLegalTerms,
          captchaToken: captcha.token || null,
        },
      },
      (response) => {
        setIsSaving(false);
        if (captcha.enabled && captcha.widgetId !== null && window.turnstile?.reset) {
          window.turnstile.reset(captcha.widgetId);
          setCaptcha((prev) => ({ ...prev, token: "" }));
        }
        if (response.status === 201) {
          setSuccessId(response.data?.id || true);
          return;
        }

        const apiFields = Array.isArray(response.error?.fields) ? response.error.fields : [];
        let hasMappedErrors = false;
        if (apiFields.length > 0) {
          const mappedErrors = {};
          apiFields.forEach((field) => {
            const reference = field?.reference;
            const message = field?.message;
            if (!reference || !message) {
              return;
            }

            mappedErrors[reference === "captchaToken" ? "captcha" : reference] = message;
          });

          if (Object.keys(mappedErrors).length > 0) {
            hasMappedErrors = true;
            setFieldErrors(mappedErrors);
          }
        }

        if (!hasMappedErrors) {
          setError(response.error?.message || "Une erreur est survenue.");
        }
      }
    );
  };

  const handleEmailBlur = () => {
    lastBlurredEmailRef.current = normalizedEmail;
    setEmailBlurLookupVersion((previous) => previous + 1);
  };

  if (loading) {
    return <p className="public-appointment-state">Chargement...</p>;
  }

  if (successId) {
    return (
      <section className="public-appointment-wrap">
        <article className="public-appointment-card">
          <h2>Rendez-vous confirmé</h2>
          <p>
            Votre demande a bien été prise en compte. Un email de confirmation vous sera envoyé prochainement. <br />Nous vous accueillerons au cabinet pour votre soin.
          </p>
          <button type="button" onClick={() => navigate("/public/catalogue")}>
            Retour au catalogue
          </button>
          <button
            type="button"
            onClick={() => window.open("https://inoutskin.com", "_blank")}
          >
            Allez au site
          </button>
        </article>
      </section>
    );
  }

  return (
    <section className="public-appointment-wrap">
      <article className="public-appointment-card">
        <h2>Prendre rendez-vous</h2>
        <div className="public-appointment-subtitle-row">
          <p className="public-appointment-subtitle">
            Choisissez votre prestation puis un creneau disponible.
          </p>
          {!showFirstAppointmentPopup && firstAppointmentBonusComplements.length > 0 && (
            <Tooltip title={'Revoir la popup'}>
              <button
                type="button"
                className="public-appointment-popup-reopen"
                onClick={() => setShowFirstAppointmentPopup(true)}
                aria-label="Revoir la popup premier rendez-vous"
                title="Revoir la popup premier rendez-vous"
              >
                <AutoAwesome fontSize="small" />
              </button>
            </Tooltip>
          )}
        </div>

        {!!error && <p className="public-appointment-error">{error}</p>}

        {showFirstAppointmentPopup && (
          <div className="public-appointment-popup-backdrop">
            <div
              className="public-appointment-popup"
              role="dialog"
              aria-modal="true"
              aria-labelledby="first-appointment-popup-title"
              aria-describedby="first-appointment-popup-description"
            >
              <p className="public-appointment-popup-badge">Offre premier rendez-vous</p>
              <h3 id="first-appointment-popup-title">1er rendez-vous chez InOut Skin</h3>
              <p className="public-appointment-popup-intro">
                Bienvenue. Vous débloquez des compléments exceptionnels réservés à votre premier rendez-vous.
              </p>
              <p className="public-appointment-popup-instruction" id="first-appointment-popup-description">
                Pour en profiter, sélectionnez le ou les compléments ci-dessous dans la section{" "}
                <strong>Compléments (optionnel)</strong>.
              </p>
              {firstAppointmentBonusComplements.length > 0 ? (
                <ul className="public-appointment-popup-list">
                  {firstAppointmentBonusComplements.map((complement) => (
                    <li key={`popup-complement-${complement.value}`} className="public-appointment-popup-item">
                      <span className="public-appointment-popup-item-icon" aria-hidden="true">+</span>
                      <span className="public-appointment-popup-item-content">
                        <span className="public-appointment-popup-item-label">{complement.label}</span>
                        <span className="public-appointment-popup-item-meta">
                          +{formatDuration(complement.duration)} · +{formatPrice(complement.price)}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="public-appointment-popup-empty">
                  Les compléments de bienvenue apparaîtront ici.
                </p>
              )}
              <button type="button" onClick={() => setShowFirstAppointmentPopup(false)}>
                Merci !
              </button>
            </div>
          </div>
        )}

        <div className="public-appointment-grid">
          <label className={`required ${fieldErrors.firstname ? "has-error" : ""}`.trim()}>
            <span className="public-appointment-label-text">Prenom</span>
            <input
              type="text"
              value={form.firstname}
              onChange={(e) => setValue("firstname", e.target.value)}
            />
            {!!fieldErrors.firstname && <span className="public-appointment-field-error">{fieldErrors.firstname}</span>}
          </label>
          <label className={`required ${fieldErrors.lastname ? "has-error" : ""}`.trim()}>
            <span className="public-appointment-label-text">Nom</span>
            <input
              type="text"
              value={form.lastname}
              onChange={(e) => setValue("lastname", e.target.value)}
            />
            {!!fieldErrors.lastname && <span className="public-appointment-field-error">{fieldErrors.lastname}</span>}
          </label>
          <label className={`full required ${fieldErrors.email ? "has-error" : ""}`.trim()}>
            <span className="public-appointment-label-text">Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setValue("email", e.target.value)}
              onBlur={handleEmailBlur}
            />
            {!!fieldErrors.email && <span className="public-appointment-field-error">{fieldErrors.email}</span>}
          </label>
          <label className={`full ${fieldErrors.telephone ? "has-error" : ""}`}>
            <span className="public-appointment-label-text">Telephone (optionnel)</span>
            <input
              type="tel"
              value={form.telephone}
              onChange={(e) => setValue("telephone", e.target.value)}
              placeholder="Ex: 0612345678 ou +33612345678"
            />
            {!!fieldErrors.telephone && <span className="public-appointment-field-error">{fieldErrors.telephone}</span>}
          </label>
          <label className={`full required ${fieldErrors.prestationId ? "has-error" : ""}`.trim()}>
            <span className="public-appointment-label-text">Prestation</span>
            <PublicSelect
              value={form.prestationId}
              onChange={(value) => setValue("prestationId", value)}
              options={prestations}
              placeholder="Selectionnez une prestation"
              groupBy={(item) => item.prestationGroupLabel}
            />
            {!!fieldErrors.prestationId && <span className="public-appointment-field-error">{fieldErrors.prestationId}</span>}
          </label>
          {!!availableComplements.length && (
            <label className={`full ${fieldErrors.complementIds ? "has-error" : ""}`}>
              <span className="public-appointment-label-text">Complements (optionnel)</span>
              <ComplementPicker
                options={availableComplements}
                value={form.complementIds}
                onChange={(nextValues) => setValue("complementIds", nextValues)}
              />
              {!!fieldErrors.complementIds && <span className="public-appointment-field-error">{fieldErrors.complementIds}</span>}
            </label>
          )}
          <label className={`required ${fieldErrors.date ? "has-error" : ""}`.trim()}>
            <span className="public-appointment-label-text">Date</span>
            <PublicDatePicker
              value={form.date}
              onChange={(nextDate) => setValue("date", nextDate)}
              minDate={today}
            />
            {!!fieldErrors.date && <span className="public-appointment-field-error">{fieldErrors.date}</span>}
          </label>
          <label className={`required ${fieldErrors.startAt ? "has-error" : ""}`.trim()}>
            <span className="public-appointment-label-text">Creneau</span>
            <PublicSelect
              value={form.startAt}
              onChange={(value) => setValue("startAt", value)}
              options={slots}
              placeholder={slotsLoading ? "Chargement..." : "Selectionnez un creneau"}
              disabled={!form.date || !form.prestationId || slotsLoading}
            />
            {!!fieldErrors.startAt && <span className="public-appointment-field-error">{fieldErrors.startAt}</span>}
          </label>
          {!!form.date && !!form.prestationId && !slotsLoading && slots.length < 1 && (
            <p className="public-appointment-warning full">
              Aucun creneau disponible pour cette date. Merci de choisir une autre date.
            </p>
          )}
          <label className="full">
            <span className="public-appointment-label-text">Message (optionnel)</span>
            <textarea
              rows={4}
              value={form.note}
              onChange={(e) => setValue("note", e.target.value)}
            />
          </label>
        </div>
        <br />
        <label className="public-appointment-legal-consent">
          <input
            type="checkbox"
            checked={!!form.acceptLegalTerms}
            onChange={(e) => setValue("acceptLegalTerms", e.target.checked)}
          />
          <span>
            En cochant cette case, je reconnais avoir pris connaissance et accepter les{" "}
            <a href="https://inoutskin.com/mentions-legales" target="_blank" rel="noreferrer">
              mentions légales
            </a>{" "}
            ainsi que les{" "}
            <a href="https://inoutskin.com/cgu" target="_blank" rel="noreferrer">
              Conditions Générales d’Utilisation (CGU)
            </a>
            .
          </span>
        </label>
        {!!fieldErrors.acceptLegalTerms && (
          <p className="public-appointment-field-error public-appointment-legal-error">
            {fieldErrors.acceptLegalTerms}
          </p>
        )}
        <br />

        {!!selectedPrestation && (
          <div className="public-appointment-summary">
            <p className="public-appointment-summary-title">Recapitulatif</p>
            <div className="public-appointment-summary-line">
              <span>{selectedPrestation.label}</span>
              <strong>{formatDuration(selectedPrestation.duration)} · {formatPrice(selectedPrestation.price)}</strong>
            </div>
            {selectedComplements.map((item) => (
              <div key={`summary-${item.value}`} className="public-appointment-summary-line is-complement">
                <span>+ {item.label}</span>
                <strong>+{formatDuration(item.duration)} · +{formatPrice(item.price)}</strong>
              </div>
            ))}
            <div className="public-appointment-summary-total">
              <span>Total</span>
              <strong>{formatDuration(totalDuration)} · {formatPrice(totalPrice)}</strong>
            </div>
          </div>
        )}

        <div className="public-appointment-actions">
          {captcha.enabled && (
            <div className="public-appointment-captcha-wrap">
              <div className="public-appointment-captcha" ref={captchaRef} />
              {!!fieldErrors.captcha && (
                <p className="public-appointment-field-error public-appointment-captcha-error">
                  {fieldErrors.captcha}
                </p>
              )}
            </div>
          )}
          <button type="button" className="btn-soft" onClick={() => navigate("/public/catalogue")}>
            Retour
          </button>

          <button type="button" className="btn-main" onClick={submit} disabled={isSaving}>
            {isSaving ? "Envoi..." : "Confirmer le rendez-vous"}
          </button>
        </div>
      </article>
    </section>
  );
}

export default PublicAppointment;
